import { useState } from "react";
import { Lightning } from "@phosphor-icons/react";
import { useOneClickTrading } from "../../hooks/useOneClickTrading";
import { Button, ButtonThemeEnum } from "../Buttons/styles";
import Toggle from "../shared/Toggle";
import { Actions, ErrorText, Label, Row, Sub, Wrapper } from "./OneClickToggle.styles";

const LABEL = "One-Click Trading";

const shortAddress = (a: string) => `${a.slice(0, 6)}…${a.slice(-4)}`;

const formatExpiry = (expiry: number): string => {
  const days = Math.max(0, Math.round((expiry - Date.now() / 1000) / 86400));
  if (days <= 0) return "expires soon";
  return `expires in ${days} day${days === 1 ? "" : "s"}`;
};

/**
 * One-Click Trading toggle. When on, the user approves a browser session key ONCE (a single wallet
 * popup) and every order afterwards signs automatically with no popup and pays no gas. When off,
 * the connected wallet signs each order (one popup per order). Renders nothing on chains where the
 * gasless relayer isn't configured.
 *
 * Turning it off asks whether to also revoke the approval on-chain (#8): deleting the local key
 * alone leaves the approval valid until it expires. A failed revoke is reported, and can be retried.
 *
 * A failed revoke is also listed separately, by address, for as long as the approval is live. The
 * retry button below only knows about the CURRENT agent, and re-enabling one-click replaces it --
 * which used to make the old, still-approved agent unreachable from anywhere in the UI.
 */
export const OneClickToggle = () => {
  const {
    supported,
    active,
    state,
    enable,
    disable,
    isEnabling,
    isDisabling,
    revokePending,
    pendingRevokes,
    revokeStrandedAgent,
    error,
  } = useOneClickTrading();
  const [confirmingOff, setConfirmingOff] = useState(false);

  if (!supported) return null;

  const busy = isEnabling || isDisabling;

  // The current agent already has its own retry button below; these are the ones
  // that would otherwise have no surface at all.
  const stranded = pendingRevokes.filter(
    (p) => !state || p.agentAddress.toLowerCase() !== state.agentAddress.toLowerCase()
  );

  const onToggle = async () => {
    if (busy) return;
    if (active) {
      setConfirmingOff(true);
      return;
    }
    try {
      await enable();
    } catch {
      /* surfaced via hook.error */
    }
  };

  const turnOff = async (revokeOnChain: boolean) => {
    setConfirmingOff(false);
    try {
      await disable(revokeOnChain);
    } catch {
      /* surfaced via hook.error; the key is kept disabled for a retry */
    }
  };

  const revokeStranded = async (agentAddress: string) => {
    try {
      await revokeStrandedAgent(agentAddress);
    } catch {
      /* surfaced via hook.error; it stays listed so it can be tried again */
    }
  };

  let status: string;
  if (isEnabling) status = "Approving session key…";
  else if (isDisabling) status = "Revoking on-chain — confirm in your wallet…";
  else if (active) {
    status = `On · orders sign instantly, no popups${state ? ` · ${formatExpiry(state.expiry)}` : ""}`;
  } else if (revokePending && state) {
    status = `Off in this browser · the on-chain approval is still valid (${formatExpiry(state.expiry)})`;
  } else {
    status = "Off · each order asks your wallet to sign. Turn on to approve once and trade in one click.";
  }

  return (
    <Wrapper>
      <Row>
        <Label>
          <Lightning size={14} weight="fill" aria-hidden="true" />
          {LABEL}
        </Label>
        <Toggle
          isOn={active}
          disabled={busy}
          ariaLabel={LABEL}
          onToggle={() => {
            void onToggle();
          }}
        />
      </Row>
      <Sub role="status">{status}</Sub>
      {confirmingOff && !busy ? (
        <>
          <Sub>
            Also revoke on-chain? It costs gas, and makes the session key useless everywhere, not
            just in this browser.
          </Sub>
          <Actions>
            <Button buttonTheme={ButtonThemeEnum.HIGHLIGHT} onClick={() => turnOff(true)}>
              Revoke (gas)
            </Button>
            <Button buttonTheme={ButtonThemeEnum.NEUTRAL2} onClick={() => turnOff(false)}>
              Just this browser
            </Button>
            <Button buttonTheme={ButtonThemeEnum.NEUTRAL3} onClick={() => setConfirmingOff(false)}>
              Cancel
            </Button>
          </Actions>
        </>
      ) : null}
      {revokePending && !active && !busy ? (
        <Actions>
          <Button buttonTheme={ButtonThemeEnum.HIGHLIGHT} onClick={() => turnOff(true)}>
            Revoke on-chain
          </Button>
        </Actions>
      ) : null}
      {stranded.length && !busy ? (
        <>
          <Sub role="alert">
            {stranded.length === 1
              ? "An older session key is still approved on-chain."
              : `${stranded.length} older session keys are still approved on-chain.`}{" "}
            A revoke did not go through, so whoever holds the key can still sign until it expires.
          </Sub>
          {stranded.map((agent) => (
            <Actions key={agent.agentAddress}>
              <Button
                buttonTheme={ButtonThemeEnum.HIGHLIGHT}
                onClick={() => {
                  void revokeStranded(agent.agentAddress);
                }}
              >
                {`Revoke ${shortAddress(agent.agentAddress)} (${formatExpiry(agent.expiry)})`}
              </Button>
            </Actions>
          ))}
        </>
      ) : null}
      {error && !busy ? <ErrorText role="alert">{error}</ErrorText> : null}
    </Wrapper>
  );
};

export default OneClickToggle;
